import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const VIEWER_MIN_DELAY = 20000;
const VIEWER_MAX_DELAY = 45000;
const VIEWER_STORAGE_KEY = 'product-viewers';
const VIEWER_ZERO_CHANCE = 0.25;

/**
 * Shows a "people are looking at this" count. The server renders a fallback count; on load this
 * picks a random count inside the configured range. The first product viewed in a session always
 * shows a count; on later products the count can be 0, which hides the notice for that product.
 * The pick is remembered per product so section re-renders (e.g. variant changes) keep it stable.
 * A visible count is nudged by one now and then, staying inside the configured range.
 *
 * @typedef {object} ProductViewersRefs
 * @property {HTMLElement} text - The text element.
 * @extends Component<ProductViewersRefs>
 */
class ProductViewers extends Component {
  requiredRefs = ['text'];

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#show(this.#pick());
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  #range() {
    const min = Math.max(1, Number(this.dataset.min) || 1);
    const max = Math.min(9, Math.max(min, Number(this.dataset.max) || 9));
    return { min, max };
  }

  /** @returns {number} */
  #pick() {
    const { min, max } = this.#range();
    const randomCount = () => min + Math.floor(Math.random() * (max - min + 1));
    const productId = this.dataset.productId ?? '';

    /** @type {{ product: string, count: number } | null} */
    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(VIEWER_STORAGE_KEY) ?? 'null');
    } catch {
      stored = null;
    }

    let count;
    if (stored && stored.product === productId && Number.isFinite(stored.count)) {
      count = stored.count;
    } else if (!stored) {
      count = randomCount();
    } else {
      count = Math.random() < VIEWER_ZERO_CHANCE ? 0 : randomCount();
    }

    try {
      sessionStorage.setItem(VIEWER_STORAGE_KEY, JSON.stringify({ product: productId, count }));
    } catch {
      // Storage unavailable: every load behaves like a first load and shows a count.
    }
    return count;
  }

  /** @param {number} count */
  #show(count) {
    this.dataset.count = String(count);
    this.hidden = count === 0;
    if (count === 0) return;
    const template = (count === 1 ? this.dataset.templateOne : this.dataset.template) ?? '';
    this.refs.text.textContent = template.replace('__COUNT__', String(count));
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (this.hidden) return;
    const delay = VIEWER_MIN_DELAY + Math.random() * (VIEWER_MAX_DELAY - VIEWER_MIN_DELAY);
    this.#timer = window.setTimeout(() => {
      this.#nudge();
      this.#schedule();
    }, delay);
  }

  #nudge() {
    if (document.hidden || this.hidden) return;

    const { min, max } = this.#range();
    const current = Number(this.dataset.count) || min;
    const next = Math.min(max, Math.max(min, current + (Math.random() < 0.5 ? -1 : 1)));
    if (next === current) return;

    this.#show(next);
  }
}

/**
 * Adds the selected variant and quantity to the cart, then goes straight to checkout.
 *
 * @typedef {object} BuyNowRefs
 * @property {HTMLButtonElement} button - The button.
 * @property {HTMLElement} loading - The loading label.
 * @property {HTMLElement} error - The error message.
 * @extends Component<BuyNowRefs>
 */
class BuyNowButton extends Component {
  requiredRefs = ['button', 'loading', 'error'];

  /** @type {AbortController | undefined} */
  #syncController;

  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    super.connectedCallback();
    this.#syncController = new AbortController();
    window.addEventListener('pageshow', this.#reset, { signal: this.#syncController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#syncController?.abort();
    this.#request?.abort();
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    const productForm = document.querySelector(`product-form-component[data-section-id="${this.dataset.sectionId}"]`);
    return productForm?.querySelector('form') ?? null;
  }

  /** Restores the button when returning from checkout via the back button. */
  #reset = () => {
    this.#setLoading(false);
  };

  /** @param {boolean} loading */
  #setLoading(loading) {
    const { button, loading: label } = this.refs;
    button.disabled = loading;
    button.setAttribute('aria-busy', String(loading));
    label.hidden = !loading;
    button.querySelector('.buy-now__label')?.toggleAttribute('hidden', loading);
  }

  /**
   * @param {MouseEvent} event
   */
  async handleClick(event) {
    event.preventDefault();

    const form = this.#form;
    const { error } = this.refs;
    error.hidden = true;
    if (!form || !form.checkValidity()) return;

    const id = /** @type {HTMLInputElement | null} */ (form.elements.namedItem('id'))?.value;
    const quantity = Number(/** @type {HTMLInputElement | null} */ (form.elements.namedItem('quantity'))?.value) || 1;
    if (!id) return;

    this.#request?.abort();
    this.#request = new AbortController();
    this.#setLoading(true);

    try {
      const config = fetchConfig('json', { body: JSON.stringify({ items: [{ id: Number(id), quantity }] }) });
      const response = await fetch(this.dataset.addUrl ?? '', { ...config, signal: this.#request.signal });
      if (!response.ok) {
        // The add was rejected (e.g. sold out): only go to checkout if the basket already has items.
        const cartResponse = await fetch('/cart.js', { signal: this.#request.signal });
        if (!cartResponse.ok) throw new Error(String(cartResponse.status));
        const cart = await cartResponse.json();
        if (cart.item_count === 0) {
          this.#setLoading(false);
          error.textContent = this.dataset.emptyCart ?? '';
          error.hidden = false;
          return;
        }
      }
      window.location.assign(this.dataset.checkoutUrl ?? '/checkout');
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      this.#setLoading(false);
      error.textContent = this.dataset.error ?? '';
      error.hidden = false;
    }
  }
}

/**
 * Free delivery message and progress bar for the shopper's region. Recalculated from /cart.js whenever the cart changes.
 *
 * @typedef {object} ProductDeliveryRefs
 * @property {HTMLElement} [messageText] - The message text (absent in regions without free delivery).
 * @property {HTMLElement} [fill] - The progress bar fill (absent in regions without free delivery).
 * @extends Component<ProductDeliveryRefs>
 */
class ProductDelivery extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal: this.#controller.signal });

    // The server can't convert the threshold into the shopper's local currency, so do it on load
    if (this.dataset.needsConversion === 'true') {
      this.#render(Number(this.dataset.cartTotal) || 0, this.dataset.cartCurrency);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#request?.abort();
  }

  #onCartUpdate = async () => {
    this.#request?.abort();
    this.#request = new AbortController();

    try {
      const response = await fetch('/cart.js', { signal: this.#request.signal });
      if (!response.ok) return;
      const cart = await response.json();
      this.#render(Number(cart.total_price) || 0, cart.currency);
    } catch {
      // Leave the current message in place if the cart can't be read.
    }
  };

  /**
   * @param {number} total - Cart total in minor units.
   * @param {string} [currency] - ISO currency code.
   */
  #render(total, currency) {
    const baseThreshold = Number(this.dataset.threshold) || 0;
    if (!baseThreshold) return;

    // Thresholds are set in the shop currency; convert to the active presentment currency and round up to a whole unit
    let threshold = baseThreshold;
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    if (currency && currency !== this.dataset.shopCurrency && rate !== 1) {
      threshold = Math.ceil((baseThreshold * rate) / 100) * 100;
    }

    const price = Number(this.dataset.variantPrice) || 0;
    const { messageText, fill } = this.refs;
    if (!messageText || !fill) return;
    const money = (/** @type {number} */ minor) =>
      new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
      }).format(minor / 100);

    // The templates are trusted theme translations; only the formatted amount is interpolated
    if (total >= threshold) messageText.innerHTML = this.dataset.msgUnlocked ?? '';
    else if (total === 0 && price >= threshold) messageText.innerHTML = this.dataset.msgQualifies ?? '';
    else if (total === 0) messageText.innerHTML = (this.dataset.msgThreshold ?? '').replace('__AMOUNT__', money(threshold));
    else messageText.innerHTML = (this.dataset.msgRemaining ?? '').replace('__AMOUNT__', money(threshold - total));

    messageText.hidden = false;
    fill.style.inlineSize = `${Math.min(100, ((total || price) / threshold) * 100)}%`;
  }
}

if (!customElements.get('product-viewers')) customElements.define('product-viewers', ProductViewers);
if (!customElements.get('buy-now-button')) customElements.define('buy-now-button', BuyNowButton);
if (!customElements.get('product-delivery')) customElements.define('product-delivery', ProductDelivery);
